import { Statistic } from 'antd';
import dayjs from 'dayjs';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { formatCountdownLabel } from './utils';

import type {
  INextAppointmentCountdownProps,
  INextAppointmentCountdownState,
} from './types';

const { Countdown } = Statistic;

const NextAppointmentCountdown = memo(
  ({
    targetDate,
    onComplete,
    textClassName,
  }: INextAppointmentCountdownProps) => {
    const [state, setState] = useState<INextAppointmentCountdownState>({});
    const completionTriggeredRef = useRef(false);

    const handleCompletion = useCallback(() => {
      if (completionTriggeredRef.current) return;
      completionTriggeredRef.current = true;
      onComplete?.();
    }, [onComplete]);

    useEffect(() => {
      completionTriggeredRef.current = false;
    }, [targetDate]);

    useEffect(() => {
      if (!targetDate) {
        setState({});
        return undefined;
      }
      const target = dayjs(targetDate);
      if (!target.isValid()) {
        setState({});
        return undefined;
      }
      let intervalId: number | null = null;
      const updateCountdown = () => {
        const now = dayjs();
        const diffMs = target.diff(now);
        setState({
          label: formatCountdownLabel(diffMs),
        });
        if (diffMs <= 0) {
          handleCompletion();
          return true;
        }
        return false;
      };
      const shouldStop = updateCountdown();
      if (shouldStop) {
        return undefined;
      }
      intervalId = window.setInterval(() => {
        if (updateCountdown()) {
          if (intervalId) {
            window.clearInterval(intervalId);
          }
        }
      }, 1000);
      return () => {
        if (intervalId) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
      };
    }, [targetDate, handleCompletion]);

    const targetTimestamp = useMemo(() => {
      if (!targetDate) return undefined;
      const parsed = dayjs(targetDate);
      return parsed.isValid() ? parsed.valueOf() : undefined;
    }, [targetDate]);

    if (!targetTimestamp) {
      return null;
    }

    return (
      <Countdown
        value={targetTimestamp}
        onFinish={handleCompletion}
        valueStyle={{ fontSize: 'inherit', color: 'inherit' }}
        className={textClassName}
        valueRender={(node) =>
          state.label ? (
            <span className={textClassName}>{state.label}</span>
          ) : (
            node
          )
        }
      />
    );
  },
);

NextAppointmentCountdown.displayName = 'NextAppointmentCountdown';

export default NextAppointmentCountdown;
